import { useSyncExternalStore } from 'react';

/**
 * Lo que está conectado a ESTA computadora (balanza por cable, cajón, pantalla táctil).
 * Se guarda en la computadora porque cada caja tiene sus propios aparatos.
 */
export type ScaleRequest = 'none' | 'ENQ' | 'W' | 'P' | 'DOLLAR';
export const SCALE_REQUEST_LABELS: Record<ScaleRequest, string> = {
  none: 'No hace falta (la balanza manda el peso sola)',
  ENQ: 'ENQ (código 05)',
  W: 'Letra W',
  P: 'Letra P',
  DOLLAR: 'Signo $',
};
export const SCALE_REQUEST_BYTES: Record<ScaleRequest, number[]> = {
  none: [],
  ENQ: [0x05],
  W: [0x57, 0x0d],
  P: [0x50, 0x0d],
  DOLLAR: [0x24],
};

export interface ScaleLinkConfig {
  enabled: boolean;
  baudRate: number;
  dataBits: 7 | 8;
  parity: 'none' | 'even' | 'odd';
  stopBits: 1 | 2;
  /** Algunas balanzas mandan el peso solo si se lo piden. */
  request: ScaleRequest;
  /** Cómo interpretar un número sin coma: auto = gramos. */
  unit: 'auto' | 'kg' | 'g';
  /** Para reconocer el mismo cable la próxima vez. */
  usbVendorId?: number;
  usbProductId?: number;
}

export interface HardwareConfig {
  scale: ScaleLinkConfig;
  /** Hay un cajón de dinero enchufado a la impresora de tickets. */
  drawer: boolean;
  /** Pantalla táctil: teclado numérico propio en pantalla. auto = se detecta solo. */
  touch: 'auto' | 'on' | 'off';
}

export const DEFAULT_SCALE_LINK: ScaleLinkConfig = {
  enabled: false,
  baudRate: 9600,
  dataBits: 8,
  parity: 'none',
  stopBits: 1,
  request: 'none',
  unit: 'auto',
};
const DEFAULT: HardwareConfig = { scale: DEFAULT_SCALE_LINK, drawer: false, touch: 'auto' };
const KEY = 'bifeai.hardware.v1';

function read(): HardwareConfig {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULT;
    const v = JSON.parse(raw) as Partial<HardwareConfig>;
    return { ...DEFAULT, ...v, scale: { ...DEFAULT_SCALE_LINK, ...(v.scale ?? {}) } };
  } catch {
    return DEFAULT;
  }
}

let current: HardwareConfig = typeof window === 'undefined' ? DEFAULT : read();
const listeners = new Set<() => void>();

export function getHardware(): HardwareConfig {
  return current;
}

export function setHardware(next: HardwareConfig): void {
  current = next;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* sin almacenamiento: queda solo mientras esté abierta la página */
  }
  listeners.forEach((l) => l());
}

export function useHardware(): HardwareConfig {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getHardware,
    getHardware,
  );
}

/** ¿La pantalla se puede tocar? (computadoras "todo en uno" táctiles, notebooks con pantalla táctil). */
export function touchDetected(): boolean {
  try {
    return window.matchMedia('(any-pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  } catch {
    return false;
  }
}

/** Modo táctil activo en esta computadora. */
export function useTouchMode(): boolean {
  const hw = useHardware();
  return hw.touch === 'on' || (hw.touch === 'auto' && touchDetected());
}
